/**
 * Base URL for API requests.
 * - VITE_API_BASE set (e.g. in .env): use it (backend may be on another port/host).
 * - file:// (Electron): query `apiBase` from main, else http://127.0.0.1:3001.
 * - Otherwise: same-origin (''). In dev Vite proxies /api, /health, /videos, etc. to backend;
 *   when built and served from backend, origin is the server. Backend must run on PORT (default 3001).
 */

/** Used for LAN-safe URL rewriting and loopback-baked build detection. */
export function isLoopbackHostname(h) {
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1';
}

/** macOS often resolves `localhost` → ::1 while Node listens on IPv4; API fetches then fail (transcription, etc.). */
function preferIpv4Loopback(base) {
  if (!base || typeof base !== 'string') return base;
  const trimmed = base.replace(/\/$/, '');
  try {
    const u = new URL(trimmed);
    if (u.hostname === 'localhost' || u.hostname === '[::1]' || u.hostname === '::1') {
      u.hostname = '127.0.0.1';
      return u.toString().replace(/\/$/, '');
    }
  } catch (_) {}
  return trimmed;
}

export function getApiBase() {
  // Electron / file:// must win over VITE_API_BASE so a mis-set build env never breaks the desktop app.
  if (typeof window !== 'undefined' && (window.location?.protocol === 'file:' || window.electronAPI)) {
    try {
      const q = new URLSearchParams(window.location?.search || '');
      const fromQuery = q.get('apiBase');
      if (fromQuery && typeof fromQuery === 'string') return preferIpv4Loopback(fromQuery);
    } catch (_) {}
    return 'http://127.0.0.1:3001';
  }
  if (import.meta.env.VITE_API_BASE) {
    const baked = preferIpv4Loopback(String(import.meta.env.VITE_API_BASE));
    // Builds sometimes bake VITE_API_BASE=http://127.0.0.1:3001. On another device (LAN), that would send API
    // traffic to that device's own loopback — broken. Use same-origin relative URLs when the page is not loopback.
    if (typeof window !== 'undefined' && window.location?.hostname) {
      try {
        const u = new URL(baked);
        if (isLoopbackHostname(u.hostname) && !isLoopbackHostname(window.location.hostname)) {
          return '';
        }
      } catch (_) {}
    }
    return baked;
  }
  return '';
}

/** Base URL for fetch(). Same as getApiBase(); in dev with proxy, use '' so requests go through Vite to backend. */
export function getFetchBase() {
  return getApiBase();
}

/**
 * /api/desktop/latest builds URLs from the request Host. If the browser used VITE_API_BASE (127.0.0.1) to
 * fetch that API, winUrl/macUrl point at localhost — broken on another device opening the app via LAN IP.
 * Rewrite loopback URLs to the current page origin so Download links hit the same host (Vite proxy or backend).
 */
export function rewriteDownloadUrlsToPageOrigin(url) {
  if (typeof window === 'undefined' || !url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, window.location.href);
    const h = window.location.hostname;
    const pageIsLoopback = isLoopbackHostname(h);
    if (pageIsLoopback) return url;
    if (isLoopbackHostname(u.hostname)) {
      return `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
    }
    // Same host, different port (e.g. API built winUrl with :3001 but the page is Vite :5173 — only :5173 is open).
    if (u.hostname === window.location.hostname) {
      const defPort = (proto) => (proto === 'https:' ? '443' : '80');
      const portA = u.port || defPort(u.protocol);
      const portB = window.location.port || defPort(window.location.protocol);
      const isDownloadPath = u.pathname === '/download/aria-windows' || u.pathname.startsWith('/download/');
      if (isDownloadPath && portA !== portB) {
        return `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
      }
    }
  } catch (_) {}
  return url;
}

/** Subscription / credits: same key as conversation (`aria_user_id` in localStorage). */
export function getAriaUserIdForApi() {
  try {
    const id = typeof localStorage !== 'undefined' ? localStorage.getItem('aria_user_id') : null;
    return id && String(id).trim() ? String(id).trim() : null;
  } catch (_) {
    return null;
  }
}

/** Spread into JSON body for transcribe/TTS when user is logged in for billing. */
export function ariaUserIdJsonBody() {
  const id = getAriaUserIdForApi();
  return id ? { userId: id } : {};
}

/**
 * Install a one-time global fetch wrapper that attaches the signed-in user's
 * Bearer SESSION TOKEN to API requests. This is what lets the backend resolve
 * the ACCOUNT identity on EVERY call (universal cross-device memory) without
 * editing every fetch site. Scoped: only /api/ URLs on our own origin/API host,
 * only when a token exists, and never overrides an explicit Authorization
 * header. Idempotent (guarded by window.__ariaAuthFetch).
 */
export function installAuthFetch() {
  if (typeof window === 'undefined' || window.__ariaAuthFetch || typeof window.fetch !== 'function') return;
  const orig = window.fetch.bind(window);
  let apiHost = null;
  try { apiHost = new URL(getApiBase() || window.location.origin, window.location.href).host; } catch (_) {}
  window.fetch = function (input, init) {
    init = init || {};
    try {
      const token = localStorage.getItem('aria_session_token');
      if (token) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        let isApi = false;
        try {
          const u = new URL(url, window.location.href);
          isApi = u.pathname.startsWith('/api/') && (u.host === window.location.host || (apiHost && u.host === apiHost));
        } catch (_) { isApi = typeof url === 'string' && url.startsWith('/api/'); }
        if (isApi) {
          const headers = new Headers((init && init.headers) || (typeof input !== 'string' && input && input.headers) || {});
          if (!headers.has('authorization')) {
            headers.set('Authorization', 'Bearer ' + token);
            init = { ...init, headers };
          }
        }
      }
    } catch (_) {}
    return orig(input, init);
  };
  window.__ariaAuthFetch = true;
}

// Install immediately on import (App imports apiBase early). Safe + idempotent.
try { installAuthFetch(); } catch (_) {}

/** Default timeout (ms) for API requests so no request can hang forever and freeze the page. */
const DEFAULT_FETCH_TIMEOUT_MS = 15000;

/**
 * fetch() with a timeout. Prevents any single request from hanging indefinitely and causing "Page Unresponsive".
 * If options.signal is provided, it is combined with the timeout (whichever fires first aborts).
 * @param {string} url
 * @param {RequestInit & { timeout?: number }} [options] - timeout in ms (default DEFAULT_FETCH_TIMEOUT_MS)
 * @returns {Promise<Response>}
 */
export function fetchWithTimeout(url, options = {}) {
  const timeoutMs = options.timeout ?? DEFAULT_FETCH_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const existingSignal = options.signal;
  if (existingSignal) {
    if (existingSignal.aborted) {
      clearTimeout(timeoutId);
      return Promise.reject(new DOMException('Aborted', 'AbortError'));
    }
    const handleAbort = () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
    existingSignal.addEventListener('abort', handleAbort);
    const { signal: _s, timeout: _t, ...rest } = options;
    return fetch(url, { ...rest, signal: controller.signal }).finally(() => {
      clearTimeout(timeoutId);
      existingSignal.removeEventListener('abort', handleAbort);
    });
  }
  const { signal: _s, timeout: _t, ...rest } = options;
  return fetch(url, { ...rest, signal: controller.signal }).finally(() => clearTimeout(timeoutId));
}

/**
 * WHOSE FAULT IS IT, ACTUALLY (Braeden 9/6)
 *
 * "when i get on the app and try to talk to the ai, it says a lot that my
 *  internet is not connected well, is that my side problem because it only
 *  happens on the app, not the website"
 *
 * It was never his internet. The desktop app posts to http://127.0.0.1:3001 -
 * his own machine - so a failed request there means Aria's backend is not
 * answering (restarting after a save, still booting, crashed), and his ISP has
 * nothing to do with it. The website is same-origin against a hosted backend,
 * which is why the message only ever showed up in the app.
 *
 * The old copy picked its wording from the BUILD MODE (`isDev ? ... : ...`),
 * and the app ships a production build - so every local outage came out as
 * "Check your internet connection and try again." Told a person the one thing
 * that was certainly not true, and pointed them at their router.
 *
 * Decide by WHERE THE REQUEST WAS GOING instead:
 *   loopback  -> her backend on this computer, which usually returns by itself
 *   remote + navigator.onLine === false -> genuinely offline, say so
 *   remote    -> the server, not the person's house
 */
export function cannotReachMessage(what = 'Aria') {
  let base = '';
  try { base = getApiBase() || ''; } catch (_) { base = ''; }
  if (!base && typeof window !== 'undefined') base = (window.location && window.location.origin) || '';
  let host = '';
  try { host = new URL(base).hostname; } catch (_) { host = ''; }
  // No parseable base in Electron means the loopback default, not the internet.
  const local = host ? isLoopbackHostname(host)
    : (typeof window !== 'undefined' && (window.electronAPI || window.location?.protocol === 'file:'));
  if (local) {
    return `Can't reach ${what} — her backend on this computer isn't answering. It normally comes back on its own in a few seconds; try again in a moment.`;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return `You're offline right now — ${what} needs a connection to answer.`;
  }
  return `Can't reach ${what} at the moment. Give it a second and try again.`;
}
